/**
 * Purpose: Save and load game state to/from localStorage for resume functionality.
 * Owns: Serialization/deserialization of GameState, single-save slot management.
 * Notes: Only one save slot is supported (no multi-save). The usedQuestionIds Set
 *        is serialized to/from an array since JSON cannot represent Sets directly.
 *        If the saved data is corrupt or from an incompatible version, loadGame
 *        returns null and the save is cleared.
 */

import type { GameState } from '../types/game';

/** localStorage key for the single save slot (CS-20: no magic strings). */
const SAVE_KEY = 'classact_saved_game';

// _______________________________________________
//              SAVE
// _______________________________________________

/**
 * Persist the current game state to localStorage.
 * Converts Set<string> (usedQuestionIds) to an array for JSON serialization.
 */
export function saveGame(state: GameState): void {
  try {
    const serializable = {
      ...state,
      usedQuestionIds: Array.from(state.usedQuestionIds),
    };
    localStorage.setItem(SAVE_KEY, JSON.stringify(serializable));
  } catch {
    /* localStorage might be full or unavailable — fail silently */
  }
}

// _______________________________________________
//              LOAD
// _______________________________________________

/**
 * Load a previously saved game from localStorage.
 * Returns the restored GameState, or null if no save exists or data is corrupt.
 */
export function loadGame(): GameState | null {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    /* Restore usedQuestionIds from array back to Set */
    if (Array.isArray(parsed.usedQuestionIds)) {
      parsed.usedQuestionIds = new Set<string>(parsed.usedQuestionIds);
    } else {
      parsed.usedQuestionIds = new Set<string>();
    }

    /* Basic shape validation — must have players array and gamePhase */
    if (!Array.isArray(parsed.players) || !parsed.gamePhase) {
      clearSave();
      return null;
    }

    return parsed as GameState;
  } catch {
    clearSave();
    return null;
  }
}

// _______________________________________________
//              CHECK & CLEAR
// _______________________________________________

/** Check whether a saved game exists in localStorage. */
export function hasSavedGame(): boolean {
  try {
    return localStorage.getItem(SAVE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Delete the saved game from localStorage. */
export function clearSave(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* fail silently */
  }
}
